/**
 * PDF Upload Wizard Component
 *
 * Feature: F005 - PDF Upload Wizard (Sprint 23)
 *
 * Provides a drag-and-drop PDF upload interface that integrates with
 * the PDF extraction pipeline. Handles upload, processing polling,
 * success, and error states.
 *
 * Flow:
 *   idle → uploading → processing (polling) → complete | failed
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { getAuthHeaders } from '@/lib/queryClient';
import { FileText, Upload, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type UploadState = 'idle' | 'uploading' | 'processing' | 'complete' | 'failed' | 'unavailable';
type RedactionStatus =
  | 'intent'
  | 'upload_leased'
  | 'queued'
  | 'processing'
  | 'redacted'
  | 'claim_leased'
  | 'submission_started'
  | 'provider_accepted'
  | 'submission_unknown'
  | 'quarantined'
  | 'cancelled'
  | 'purged'
  | 'expired';

export interface UploadFailureState {
  kind: 'failed' | 'unavailable';
  message: string;
}

export function uploadFailureState(status?: number): UploadFailureState {
  if (status === 503) {
    return {
      kind: 'unavailable',
      message: 'PDF extraction is temporarily unavailable. Please try again later.',
    };
  }
  return { kind: 'failed', message: 'Upload failed. Please try again.' };
}

export function pdfUploadUnavailableView() {
  return {
    title: 'PDF extraction is temporarily unavailable',
    description: 'Please try again later. Your document was not sent for extraction.',
    actions: [] as string[],
  };
}

export function PdfUploadUnavailable() {
  const view = pdfUploadUnavailableView();
  return (
    <div className="space-y-4 py-4 text-center" data-testid="pdf-extraction-unavailable">
      <AlertCircle className="h-10 w-10 text-destructive mx-auto" />
      <div>
        <p className="text-sm font-semibold text-destructive">
          {view.title}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {view.description}
        </p>
      </div>
    </div>
  );
}

interface ExtractionJob {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  errorMessage?: string;
  resultSummary?: {
    profitLossItems: number;
    balanceSheetItems: number;
    confidence: number;
  };
}

interface UploadResponse {
  jobId: string;
  status: RedactionStatus;
  expiresAt: string;
  safeReasonCode?: string;
}

interface UploadIntentResponse extends UploadResponse {
  status: 'intent';
  uploadExpiresAt?: string;
  uploadUrl: string;
  uploadCapability: string;
}

interface ResumeResponse {
  job: {
    jobId: string;
    status: RedactionStatus;
    expiresAt: string | null;
    safeReasonCode?: string | null;
  } | null;
}

interface SubmitResponse {
  jobId: string;
  status: 'processing';
  providerRunId: string;
  estimatedSeconds: number;
}

export interface PdfUploadWizardProps {
  /**
   * Returns the financial period ID to associate with the upload.
   * Called lazily on the first upload attempt so the period is only
   * created when the user actually starts the upload.
   */
  getPeriodId: () => Promise<string>;
  /** Called when extraction completes — passes the job ID for the preview step */
  onComplete: (jobId: string) => void;
  /** Called when user clicks "Use Manual Entry" fallback */
  onManualEntry: () => void;
  /** Called when user clicks Back — navigates to method selection step */
  onBack?: () => void;
  /** Called when user clicks Cancel — closes the wizard */
  onCancel?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB
const RESUME_STORAGE_KEY = 'crmx:tfn-redaction-resume';

interface StoredResumeReference {
  periodId: string;
  jobId: string;
  expiresAt: string;
}

function isUuid(value: unknown): value is string {
  return typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function readStoredResumeReference(
  storage: Pick<Storage, 'getItem' | 'removeItem'> | undefined =
    typeof localStorage === 'undefined' ? undefined : localStorage,
): StoredResumeReference | null {
  if (!storage) return null;
  try {
    const parsed = JSON.parse(
      storage.getItem(RESUME_STORAGE_KEY) ?? 'null',
    ) as Partial<StoredResumeReference> | null;
    if (
      !parsed
      || !isUuid(parsed.periodId)
      || !isUuid(parsed.jobId)
      || typeof parsed.expiresAt !== 'string'
      || !Number.isFinite(new Date(parsed.expiresAt).valueOf())
      || new Date(parsed.expiresAt).valueOf() <= Date.now()
    ) {
      storage.removeItem(RESUME_STORAGE_KEY);
      return null;
    }
    return {
      periodId: parsed.periodId,
      jobId: parsed.jobId,
      expiresAt: parsed.expiresAt,
    };
  } catch {
    storage.removeItem(RESUME_STORAGE_KEY);
    return null;
  }
}

function storeResumeReference(reference: StoredResumeReference): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify(reference));
}

function clearResumeReference(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(RESUME_STORAGE_KEY);
}

async function pollUntilRedacted(
  createdJobId: string,
  authHeaders: Record<string, string>,
  signal: AbortSignal,
  onProgress: () => void,
): Promise<'redacted' | 'provider_accepted'> {
  while (true) {
    await wait(1_000, signal);
    const statusRes = await fetch(
      `/api/financial/extract/${createdJobId}/redaction-status`,
      {
        headers: authHeaders,
        credentials: 'include',
        signal,
      },
    );
    if (!statusRes.ok) {
      throw new Error('Redaction status check failed');
    }
    const statusData = await statusRes.json() as UploadResponse;
    if (
      statusData.expiresAt
      && new Date(statusData.expiresAt).valueOf() <= Date.now()
    ) {
      throw new Error('The protected upload expired before redaction completed');
    }
    if (statusData.status === 'redacted') return 'redacted';
    if (statusData.status === 'provider_accepted') {
      return 'provider_accepted';
    }
    if (
      statusData.status === 'submission_started'
      || statusData.status === 'submission_unknown'
    ) {
      throw new Error(
        'Provider submission requires reconciliation and will not be retried automatically',
      );
    }
    if (![
      'intent',
      'upload_leased',
      'queued',
      'processing',
      'claim_leased',
    ].includes(statusData.status)) {
      throw new Error(
        `The document could not be safely redacted (${statusData.safeReasonCode ?? statusData.status})`,
      );
    }
    onProgress();
  }
}

async function submitRedacted(
  createdJobId: string,
  financialPeriodId: string,
  authHeaders: Record<string, string>,
  signal: AbortSignal,
): Promise<SubmitResponse> {
  const submitRes = await fetch(
    `/api/financial/extract/${createdJobId}/submit`,
    {
      method: 'POST',
      headers: {
        ...authHeaders,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ financialPeriodId }),
      credentials: 'include',
      signal,
    },
  );
  if (!submitRes.ok) {
    const failure = await submitRes.json().catch(() => null) as {
      status?: RedactionStatus;
      error?: string;
    } | null;
    if (
      failure?.status === 'submission_started'
      || failure?.status === 'submission_unknown'
    ) {
      throw new Error(
        'Provider submission requires reconciliation and will not be retried automatically',
      );
    }
    throw new Error(failure?.error ?? 'Provider submission failed');
  }
  return submitRes.json() as Promise<SubmitResponse>;
}

export function uploadPdfDirect(
  intent: Pick<UploadIntentResponse, 'uploadUrl' | 'uploadCapability'>,
  file: File,
  onProgress: (percentage: number) => void,
  signal?: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    request.open('POST', intent.uploadUrl);
    request.setRequestHeader('Content-Type', 'application/pdf');
    request.setRequestHeader(
      'x-ocr-upload-capability',
      intent.uploadCapability,
    );
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(100, Math.round(
          (event.loaded / event.total) * 100,
        )));
      }
    });
    request.addEventListener('load', () => {
      signal?.removeEventListener('abort', abort);
      if (request.status === 202) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error('The protected PDF upload was not accepted'));
      }
    });
    request.addEventListener('error', () => {
      signal?.removeEventListener('abort', abort);
      reject(new Error('The protected PDF upload could not be completed'));
    });
    request.addEventListener('abort', () => {
      signal?.removeEventListener('abort', abort);
      reject(new DOMException('Upload cancelled', 'AbortError'));
    });
    if (signal?.aborted) {
      request.abort();
      return;
    }
    signal?.addEventListener('abort', abort, { once: true });
    request.send(file);
  });
}

function wait(milliseconds: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, milliseconds);
    signal.addEventListener('abort', () => {
      clearTimeout(timeout);
      reject(new DOMException('Upload cancelled', 'AbortError'));
    }, { once: true });
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PdfUploadWizard({ getPeriodId, onComplete, onManualEntry, onBack, onCancel }: PdfUploadWizardProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [periodError, setPeriodError] = useState<string>('');
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const elapsedIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeJobIdRef = useRef<string | null>(null);

  // -------------------------------------------------------------------------
  // Elapsed timer (runs during processing)
  // -------------------------------------------------------------------------

  useEffect(() => {
    if (uploadState === 'processing') {
      elapsedIntervalRef.current = setInterval(() => {
        setElapsedSeconds((s) => s + 1);
      }, 1000);
    } else {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
        elapsedIntervalRef.current = null;
      }
      setElapsedSeconds(0);
    }
    return () => {
      if (elapsedIntervalRef.current) {
        clearInterval(elapsedIntervalRef.current);
      }
    };
  }, [uploadState]);

  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  // Rehydrate the last opaque tenant-owned bridge reference after refresh or
  // tab close. No capability, filename, object key or document byte is stored.
  useEffect(() => {
    const stored = readStoredResumeReference();
    if (!stored) return;
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setUploadState('uploading');
    setUploadProgress(60);
    setJobId(stored.jobId);
    activeJobIdRef.current = stored.jobId;

    void (async () => {
      try {
        const authHeaders = await getAuthHeaders();
        const resumeRes = await fetch(
          `/api/financial/extract/resume/${stored.periodId}`,
          {
            headers: authHeaders,
            credentials: 'include',
            signal: controller.signal,
          },
        );
        if (!resumeRes.ok) {
          clearResumeReference();
          activeJobIdRef.current = null;
          setJobId(null);
          setUploadState('idle');
          return;
        }
        const resumed = (await resumeRes.json() as ResumeResponse).job;
        if (!resumed) {
          clearResumeReference();
          activeJobIdRef.current = null;
          setJobId(null);
          setUploadState('idle');
          return;
        }
        setJobId(resumed.jobId);
        activeJobIdRef.current = resumed.jobId;
        if (resumed.status === 'provider_accepted') {
          setUploadProgress(100);
          setUploadState('processing');
          return;
        }
        if (
          resumed.status === 'submission_started'
          || resumed.status === 'submission_unknown'
        ) {
          setErrorMessage(
            'Provider submission requires reconciliation and will not be retried automatically',
          );
          setUploadState('failed');
          return;
        }
        if (
          resumed.status === 'intent'
          || resumed.status === 'upload_leased'
        ) {
          await fetch(
            `/api/financial/extract/${resumed.jobId}/cancel`,
            {
              method: 'POST',
              headers: authHeaders,
              credentials: 'include',
              signal: controller.signal,
            },
          );
          clearResumeReference();
          activeJobIdRef.current = null;
          setJobId(null);
          setUploadProgress(0);
          setPeriodError(
            'The previous upload was interrupted before it completed. Select the PDF again.',
          );
          setUploadState('idle');
          return;
        }
        const redactionState = resumed.status === 'redacted'
          ? 'redacted'
          : await pollUntilRedacted(
              resumed.jobId,
              authHeaders,
              controller.signal,
              () => setUploadProgress((progress) => Math.max(progress, 60)),
            );
        if (redactionState === 'provider_accepted') {
          setUploadProgress(100);
          setUploadState('processing');
          return;
        }
        setUploadProgress(75);
        const submitted = await submitRedacted(
          resumed.jobId,
          stored.periodId,
          authHeaders,
          controller.signal,
        );
        setJobId(submitted.jobId);
        activeJobIdRef.current = submitted.jobId;
        setUploadProgress(100);
        setUploadState('processing');
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'The protected PDF job could not be resumed',
        );
        setUploadState('failed');
      } finally {
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    })();

    return () => controller.abort();
  }, []);

  // -------------------------------------------------------------------------
  // Polling: GET /api/financial/extract/:jobId/status
  // -------------------------------------------------------------------------

  const { data: statusData } = useQuery<ExtractionJob>({
    queryKey: ['/api/financial/extract', jobId, 'status'],
    enabled: uploadState === 'processing' && Boolean(jobId),
    refetchInterval: uploadState === 'processing' ? 5000 : false,
    queryFn: async () => {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`/api/financial/extract/${jobId}/status`, {
        headers: authHeaders,
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`Status check failed: ${res.status}`);
      }
      return res.json();
    },
  });

  // React to status changes
  useEffect(() => {
    if (!statusData) return;

    if (statusData.status === 'completed') {
      clearResumeReference();
      activeJobIdRef.current = null;
      setUploadState('complete');
      queryClient.invalidateQueries({ queryKey: ['/api/financial/extract'] });
      toast({
        title: 'Extraction complete',
        description: `Found ${statusData.resultSummary?.profitLossItems ?? 0} P&L items and ${statusData.resultSummary?.balanceSheetItems ?? 0} Balance Sheet items`,
      });
      // Auto-transition after 2 seconds
      const timer = setTimeout(() => {
        if (jobId) onComplete(jobId);
      }, 2000);
      return () => clearTimeout(timer);
    }

    if (statusData.status === 'failed') {
      setUploadState('failed');
      setErrorMessage(statusData.errorMessage ?? 'Extraction failed. Please try again.');
      toast({
        title: 'PDF extraction failed',
        description: statusData.errorMessage ?? 'Extraction failed',
        variant: 'destructive',
      });
    }
  }, [statusData, jobId, onComplete, queryClient, toast]);

  // -------------------------------------------------------------------------
  // File validation
  // -------------------------------------------------------------------------

  const validateFile = useCallback(
    (file: File): boolean => {
      if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
        toast({
          title: 'Invalid file type',
          description: 'Please select a PDF file (.pdf)',
          variant: 'destructive',
        });
        return false;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast({
          title: 'File too large',
          description: 'PDF must be smaller than 20 MB',
          variant: 'destructive',
        });
        return false;
      }
      return true;
    },
    [toast]
  );

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!validateFile(file)) return;
      setSelectedFile(file);
    },
    [validateFile]
  );

  // -------------------------------------------------------------------------
  // Drag and drop handlers
  // -------------------------------------------------------------------------

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFileSelect(file);
    },
    [handleFileSelect]
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const focusDropzone = useCallback(() => {
    requestAnimationFrame(() => dropzoneRef.current?.focus());
  }, []);

  // -------------------------------------------------------------------------
  // Upload and extraction pipeline
  // -------------------------------------------------------------------------

  const handleUploadAndExtract = useCallback(async () => {
    if (!selectedFile) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setUploadState('uploading');
    setUploadProgress(0);
    setErrorMessage('');

    try {
      // 1. Resolve the period ID (creates period if not yet created)
      const resolvedPeriodId = await getPeriodId();

      // Guard: block submit if period ID is undefined/null/empty
      if (!resolvedPeriodId) {
        setPeriodError('Please complete the period details first');
        setUploadState('idle');
        return;
      }

      const authHeaders = await getAuthHeaders();

      const pollProtected = (createdJobId: string) => pollUntilRedacted(
        createdJobId,
        authHeaders,
        controller.signal,
        () => setUploadProgress((progress) => Math.max(progress, 60)),
      );

      const submitProtected = async (createdJobId: string): Promise<void> => {
        setUploadProgress(75);
        const submitData = await submitRedacted(
          createdJobId,
          resolvedPeriodId,
          authHeaders,
          controller.signal,
        );
        setJobId(submitData.jobId);
        activeJobIdRef.current = submitData.jobId;
        setUploadProgress(100);
        setUploadState('processing');
      };

      // Resume an already uploaded job without minting another capability.
      const resumeRes = await fetch(
        `/api/financial/extract/resume/${resolvedPeriodId}`,
        {
          headers: authHeaders,
          credentials: 'include',
          signal: controller.signal,
        },
      );
      if (resumeRes.ok) {
        const resumed = (await resumeRes.json() as ResumeResponse).job;
        if (resumed) {
          setJobId(resumed.jobId);
          activeJobIdRef.current = resumed.jobId;
          if (resumed.status === 'provider_accepted') {
            setUploadProgress(100);
            setUploadState('processing');
            return;
          }
          if (
            resumed.status === 'submission_started'
            || resumed.status === 'submission_unknown'
          ) {
            throw new Error(
              'Provider submission requires reconciliation and will not be retried automatically',
            );
          }
          if (resumed.status === 'redacted') {
            await submitProtected(resumed.jobId);
            return;
          }
          if ([
            'queued',
            'processing',
            'claim_leased',
          ].includes(resumed.status)) {
            const resumedState = await pollProtected(resumed.jobId);
            if (resumedState === 'redacted') {
              await submitProtected(resumed.jobId);
            } else {
              setUploadProgress(100);
              setUploadState('processing');
            }
            return;
          }
          if (
            resumed.status === 'intent'
            || resumed.status === 'upload_leased'
          ) {
            await fetch(
              `/api/financial/extract/${resumed.jobId}/cancel`,
              {
                method: 'POST',
                headers: authHeaders,
                credentials: 'include',
                signal: controller.signal,
              },
            );
          }
        }
      }

      // Create a metadata-only intent through Vercel.
      const intentRes = await fetch('/api/financial/extract/upload-intent', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
          'Idempotency-Key': `intent:${crypto.randomUUID()}`,
        },
        body: JSON.stringify({
          financialPeriodId: resolvedPeriodId,
          contentType: 'application/pdf',
          contentLength: selectedFile.size,
        }),
        credentials: 'include',
        signal: controller.signal,
      });
      if (!intentRes.ok) {
        if (intentRes.status === 503) {
          const failure = uploadFailureState(intentRes.status);
          setErrorMessage(failure.message);
          setUploadState(failure.kind);
          return;
        }
        throw new Error('The protected upload intent could not be created');
      }
      const intent = await intentRes.json() as UploadIntentResponse;
      setJobId(intent.jobId);
      activeJobIdRef.current = intent.jobId;
      storeResumeReference({
        periodId: resolvedPeriodId,
        jobId: intent.jobId,
        expiresAt: intent.expiresAt,
      });

      // Send the File directly to the capability-gated Worker. Cookies and
      // Vercel are not part of this byte path.
      await uploadPdfDirect(
        intent,
        selectedFile,
        (percentage) => setUploadProgress(Math.round(percentage * 0.55)),
        controller.signal,
      );
      const redactionStatus = await pollProtected(intent.jobId);
      if (redactionStatus === 'redacted') {
        await submitProtected(intent.jobId);
      } else {
        setUploadProgress(100);
        setUploadState('processing');
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      const msg = err instanceof Error ? err.message : 'Upload failed';
      setErrorMessage(msg);
      setUploadState('failed');
      toast({
        title: 'Upload failed',
        description: msg,
        variant: 'destructive',
      });
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [selectedFile, getPeriodId, toast]);

  const handleCancelActive = useCallback(async (): Promise<boolean> => {
    abortControllerRef.current?.abort();
    const activeJobId = activeJobIdRef.current;
    if (!activeJobId) {
      clearResumeReference();
      setUploadState('idle');
      focusDropzone();
      return true;
    }
    try {
      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        `/api/financial/extract/${activeJobId}/cancel`,
        {
          method: 'POST',
          headers: authHeaders,
          credentials: 'include',
        },
      );
      if (!response.ok) throw new Error('Cancellation could not be confirmed');
      const result = await response.json() as UploadResponse;
      if (result.status === 'cancelled') {
        clearResumeReference();
        activeJobIdRef.current = null;
        setJobId(null);
        setUploadProgress(0);
        setUploadState('idle');
        focusDropzone();
        return true;
      }
      setErrorMessage(
        'Provider submission may already have started. The job is held for reconciliation and will not be retried automatically.',
      );
      setUploadState('failed');
      return false;
    } catch {
      setErrorMessage('Cancellation could not be confirmed. Check the job status before retrying.');
      setUploadState('failed');
      return false;
    }
  }, [focusDropzone]);

  const handleRetry = useCallback(async () => {
    abortControllerRef.current?.abort();
    const activeJobId = activeJobIdRef.current;
    if (activeJobId) {
      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(
          `/api/financial/extract/${activeJobId}/cancel`,
          {
            method: 'POST',
            headers: authHeaders,
            credentials: 'include',
          },
        );
        if (!response.ok) {
          throw new Error('Cancellation could not be confirmed');
        }
        const result = await response.json() as UploadResponse;
        if (result.status !== 'cancelled') {
          setErrorMessage(
            'Provider submission requires reconciliation and will not be retried automatically',
          );
          return;
        }
      } catch {
        setErrorMessage(
          'The previous job could not be cancelled safely. Check its status before retrying.',
        );
        return;
      }
    }
    clearResumeReference();
    activeJobIdRef.current = null;
    setUploadState('idle');
    setJobId(null);
    setSelectedFile(null);
    setErrorMessage('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleManualEntry = useCallback(async () => {
    if (!await handleCancelActive()) return;
    clearResumeReference();
    onManualEntry();
  }, [handleCancelActive, onManualEntry]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // --- State: uploading ---
  if (uploadState === 'uploading') {
    return (
      <div
        className="space-y-4 py-4"
        data-testid="processing-status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
          <span className="text-sm font-medium">
            Protecting and preparing {selectedFile?.name ?? 'your document'}…
          </span>
        </div>
        <Progress value={uploadProgress} className="h-2" />
        <p className="text-xs text-muted-foreground">
          {uploadProgress}% complete
        </p>
        <Button
          variant="outline"
          onClick={handleCancelActive}
          data-testid="button-cancel-active-upload"
        >
          Cancel upload
        </Button>
      </div>
    );
  }

  // --- State: processing ---
  if (uploadState === 'processing') {
    return (
      <div
        className="space-y-4 py-4"
        data-testid="processing-status"
        aria-live="polite"
      >
        <div className="flex flex-col items-center justify-center gap-3 py-6 text-center">
          <div className="relative">
            <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
          </div>
          <div>
            <p className="text-sm font-medium">
              Extracting financial data from {selectedFile?.name ?? 'your document'}…
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Elapsed: {elapsedSeconds}s
            </p>
          </div>
          <p className="text-xs text-muted-foreground">
            Processing with the selected extraction provider
          </p>
          <Button
            variant="outline"
            onClick={handleCancelActive}
            data-testid="button-cancel-active-upload"
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // --- State: complete ---
  if (uploadState === 'complete' && statusData?.status === 'completed') {
    const summary = statusData.resultSummary;
    return (
      <div className="space-y-4 py-4 text-center" data-testid="processing-status">
        <CheckCircle className="h-10 w-10 text-green-500 mx-auto" />
        <div>
          <p className="text-sm font-semibold text-green-700">
            Financial data extracted successfully
          </p>
          {summary && (
            <p className="text-xs text-muted-foreground mt-1">
              Found {summary.profitLossItems} P&L items and {summary.balanceSheetItems} Balance Sheet items
            </p>
          )}
          {summary && (
            <p className="text-xs text-muted-foreground">
              Match confidence: {Math.round(summary.confidence * 100)}%
            </p>
          )}
        </div>
        <Button
          onClick={() => jobId && onComplete(jobId)}
          data-testid="button-review-extracted"
        >
          Review Extracted Data
        </Button>
      </div>
    );
  }

  // --- State: failed ---
  if (uploadState === 'unavailable') {
    return <PdfUploadUnavailable />;
  }

  // --- State: failed ---
  if (uploadState === 'failed') {
    return (
      <div className="space-y-4 py-4 text-center">
        <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
        <div>
          <p className="text-sm font-semibold text-red-700">Extraction failed</p>
          <p className="text-xs text-muted-foreground mt-1">{errorMessage}</p>
        </div>
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={handleRetry} data-testid="button-retry-upload">
            Try Again
          </Button>
          <Button variant="outline" onClick={handleManualEntry} data-testid="button-manual-entry">
            Use Manual Entry
          </Button>
        </div>
      </div>
    );
  }

  // --- State: idle (default) ---
  return (
    <div className="space-y-4">
      {/* Dropzone */}
      <div
        ref={dropzoneRef}
        className={[
          'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400',
        ].join(' ')}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !selectedFile && fileInputRef.current?.click()}
        onKeyDown={(event) => {
          if (!selectedFile && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label="Select a PDF document"
        data-testid="dropzone-pdf-upload"
      >
        {selectedFile ? (
          /* File selected state */
          <div className="flex flex-col items-center gap-2">
            <FileText className="h-8 w-8 text-blue-500" />
            <p className="text-sm font-medium">{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground">
              {formatFileSize(selectedFile.size)}
            </p>
            <p className="text-xs text-muted-foreground">
              Supported: Accountant-prepared financials, tax returns
            </p>
          </div>
        ) : (
          /* Empty state */
          <div className="flex flex-col items-center gap-2">
            <Upload className="h-8 w-8 text-gray-400" />
            <p className="text-sm text-gray-500">
              Drop your PDF here or click to browse
            </p>
            <p className="text-xs text-muted-foreground">PDF only, max 20 MB</p>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleFileInputChange}
        />
      </div>

      {/* Period error message */}
      {periodError && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-3" data-testid="period-error">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <p className="text-sm text-destructive">{periodError}</p>
          </div>
        </div>
      )}

      {/* Action row: file controls */}
      <div className="flex justify-between items-center">
        {selectedFile ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemoveFile}
            data-testid="button-remove-file"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        ) : (
          <span />
        )}

        <Button
          onClick={handleUploadAndExtract}
          disabled={!selectedFile}
          data-testid="button-upload-extract"
        >
          <Upload className="h-4 w-4 mr-2" />
          Upload &amp; Extract
        </Button>
      </div>

      {/* Navigation row: Back, Cancel, Use Manual Entry */}
      <div className="flex justify-between items-center pt-2 border-t">
        <div className="flex gap-2">
          {onBack && (
            <Button variant="outline" onClick={onBack} data-testid="button-wizard-back">
              Back
            </Button>
          )}
          {onCancel && (
            <Button variant="ghost" onClick={onCancel} data-testid="button-wizard-cancel">
              Cancel
            </Button>
          )}
        </div>
        <Button variant="ghost" onClick={handleManualEntry} data-testid="button-manual-entry">
          Use Manual Entry
        </Button>
      </div>
    </div>
  );
}
